import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { toast as message } from 'sonner';
import SectionLabel from './atoms/SectionLabel';
import RecordLookupSelect from '../../RecordLookupSelect';
import { useGlobalMetadata } from '../../../utils/providers';
import { useMyCapabilities } from '../../../utils/metadata/capabilities';
import { useEditLocks } from '../../../hooks/useEditLocks';
import { TAB_SESSION_ID } from '../../../utils/realtime/editLocks';
import { updateRecord, handleOperationError } from '../../../utils/supabase-operations';
import { invalidateAndRefreshAllQueriesForTable } from '../../../utils/metadata';
import { requestReportGridRefresh } from '../../../utils/reportGridRefresh';
import type { RelationshipLookup } from '../../../types/metadata';
import type { BaseRecord } from '../../../types/api';

interface MerchantBasicInfoTabProps {
  merchant: Record<string, any> | null;
  isLoading: boolean;
}

/**
 * Fields Opener/Closer may edit even without the edit_deals capability
 * (per RBAC spec: notes + additional contacts only). Mirrors the DB-side
 * column-guard trigger in supabase/pg/rbac/deal_rls.sql — keep in sync.
 */
const REP_WRITABLE_FIELDS = new Set(['note', 'alternate_phone', 'alternate_phone2', 'alternative_email', 'ContactMethod']);

type FieldType = 'text' | 'textarea' | 'currency' | 'picklist' | 'relationship';

interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  /** For type 'relationship': the relationship_lookup col_name (usually === key). */
  relationshipField?: string;
}

// REG-812: the fields admins change most sit on top; the rest follow the
// merchant page's groups and order (merchant_default_detail).
const QUICK_FIELDS: FieldDef[] = [
  { key: 'status', label: 'Status', type: 'picklist' },
  { key: 'merchant_response', label: 'Merchant Feedback', type: 'picklist' },
  { key: 'requesting', label: 'Requesting', type: 'currency' },
  { key: 'lead_source', label: 'Lead Source', type: 'relationship', relationshipField: 'lead_source' },
  { key: 'MarketingBrandDB', label: 'Marketing Brand', type: 'picklist' },
  { key: 'main_rep', label: 'Main Rep', type: 'relationship', relationshipField: 'main_rep' },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'email', label: 'Email', type: 'text' },
];

const CONTACT_FIELDS: FieldDef[] = [
  { key: 'business_name', label: 'Business Name', type: 'text' },
  { key: 'dba_name', label: 'DBA', type: 'text' },
  { key: 'first_name', label: 'First Name', type: 'text' },
  { key: 'last_name', label: 'Last Name', type: 'text' },
  { key: 'alternate_phone', label: 'Alt Phone', type: 'text' },
  { key: 'alternate_phone2', label: 'Alt Phone 2', type: 'text' },
  { key: 'alternative_email', label: 'Alt Email', type: 'text' },
  { key: 'website', label: 'Website', type: 'text' },
  { key: 'ContactMethod', label: 'Contact Method', type: 'picklist' },
];

const BUSINESS_FIELDS: FieldDef[] = [
  { key: 'ein', label: 'EIN', type: 'text' },
  { key: 'industry', label: 'Industry', type: 'text' },
  { key: 'date_business_started', label: 'Date Started', type: 'text' },
  { key: 'monthly_revenue', label: 'Monthly Rev', type: 'currency' },
  { key: 'annual_revenue', label: 'Annual Rev', type: 'currency' },
  { key: 'ownership_percentage', label: 'Ownership %', type: 'text' },
  { key: 'credit_score', label: 'Credit Score', type: 'text' },
];

const ADDRESS_FIELDS: FieldDef[] = [
  { key: 'street_num', label: 'Street #', type: 'text' },
  { key: 'street', label: 'Street', type: 'text' },
  { key: 'city', label: 'City', type: 'text' },
  { key: 'state', label: 'State', type: 'text' },
  { key: 'zip', label: 'Zip', type: 'text' },
];

const NOTE_FIELD: FieldDef = { key: 'note', label: 'Note', type: 'textarea' };

function MerchantBasicInfoTab({ merchant, isLoading }: MerchantBasicInfoTabProps) {
  const { data: metadata } = useGlobalMetadata();
  const { hasCapability, isLoading: capabilitiesLoading } = useMyCapabilities();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  // The field the user clicked to start editing gets the focus.
  const [focusKey, setFocusKey] = useState<string | null>(null);

  const canEditEverything = hasCapability('edit_deals');
  const relationshipsByField: Record<string, RelationshipLookup> =
    (metadata as any)?.relationshipLookupData?.merchant?.byFieldName || {};
  const picklistsByField: Record<string, { values: Array<{ value: string; label?: string }> }> =
    (metadata as any)?.picklistData?.merchant?.byFieldName || {};

  // Live edit lock on the whole merchant record, shared with the grid's per-cell locks.
  const [, bumpLocks] = useReducer((n: number) => n + 1, 0);
  const { getLockHolder, acquire, releaseAll } = useEditLocks({
    schema: 'public',
    tableName: 'merchant',
    onChange: bumpLocks,
    enabled: Boolean(merchant?.id),
  });

  const lockHolder = merchant?.id != null ? getLockHolder(merchant.id) : null;

  // The panel is reused when a different merchant is opened — drop the lock on the old one
  // instead of holding it until the panel closes.
  useEffect(() => () => {
    releaseAll();
    setEditing(false);
    setDraft({});
  }, [merchant?.id, releaseAll]);

  const startEditing = useCallback((fieldKey?: string) => {
    if (!merchant) return;
    setFocusKey(fieldKey ?? null);
    const holder = getLockHolder(merchant.id);
    if (holder) {
      message.error(`${holder.userName} is editing this merchant right now.`);
      return;
    }
    acquire(merchant.id);
    setDraft({ ...merchant });
    setEditing(true);
  }, [merchant, getLockHolder, acquire]);

  const cancelEditing = useCallback(() => {
    releaseAll();
    setEditing(false);
    setDraft({});
  }, [releaseAll]);

  // Two tabs can claim the record inside one presence round-trip, so both may end up in
  // edit mode. Both sides run the same comparison and the lower session id keeps the lock.
  useEffect(() => {
    if (!editing || saving || !lockHolder) return;
    if (lockHolder.sessionId >= TAB_SESSION_ID) return;
    releaseAll();
    setEditing(false);
    setDraft({});
    message.error(`${lockHolder.userName} opened this merchant for editing first — your changes were not saved.`);
  }, [editing, saving, lockHolder, releaseAll]);

  const setField = useCallback((key: string, value: any) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  const allFields = useMemo(
    () => [...QUICK_FIELDS, NOTE_FIELD, ...CONTACT_FIELDS, ...BUSINESS_FIELDS, ...ADDRESS_FIELDS],
    [],
  );

  const isFieldWritable = useCallback(
    (key: string) => canEditEverything || REP_WRITABLE_FIELDS.has(key),
    [canEditEverything],
  );

  const handleSave = useCallback(async () => {
    if (!merchant) return;
    const patch: Record<string, any> = {};
    const expectedValues: Record<string, any> = {};
    const expectedValueTypes: Record<string, string | undefined> = {};
    for (const fieldDef of allFields) {
      if (!isFieldWritable(fieldDef.key)) continue;
      const nextValue = draft[fieldDef.key] ?? null;
      const prevValue = merchant[fieldDef.key] ?? null;
      if (nextValue !== prevValue) {
        patch[fieldDef.key] = nextValue === '' ? null : nextValue;
        expectedValues[fieldDef.key] = prevValue;
        expectedValueTypes[fieldDef.key] = (metadata as any)?.fieldData?.merchant?.byFieldName?.[fieldDef.key]?.data_type;
      }
    }
    if (Object.keys(patch).length === 0) {
      releaseAll();
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const result = await updateRecord({
        tableName: 'merchant',
        schema: 'public',
        data: patch,
        whereClause: { id: merchant.id },
        // The lock keeps two reps out of the same record; this catches whoever slipped
        // past it (stale tab, a write from outside the browser).
        options: { invalidateCache: false, expectedValues, expectedValueTypes },
      });
      if (result.error) throw result.error;
      await invalidateAndRefreshAllQueriesForTable('merchant');
      requestReportGridRefresh();
      message.success('Saved');
      releaseAll();
      setEditing(false);
    } catch (error) {
      const info = handleOperationError(error, 'Failed to save merchant');
      message.error(info.userMessage);
      // Stay in edit mode — and keep the lock — so the rep doesn't lose what they typed.
    } finally {
      setSaving(false);
    }
  }, [merchant, draft, allFields, isFieldWritable, metadata, releaseAll]);

  if (isLoading || capabilitiesLoading) return <div className="sky-mp-empty">Loading...</div>;
  if (!merchant) return <div className="sky-mp-empty">No merchant data found.</div>;

  const m = editing ? draft : merchant;

  const renderField = (fieldDef: FieldDef) => {
    const writable = isFieldWritable(fieldDef.key);
    if (!editing || !writable) {
      const displayValue = fieldDef.type === 'currency'
        ? formatMoney(m[fieldDef.key])
        : fieldDef.type === 'relationship'
          ? m[`${fieldDef.key}_rel`]?.[relationshipsByField[fieldDef.relationshipField || fieldDef.key]?.target_label_field || ''] ?? m[fieldDef.key]
          : m[fieldDef.key];
      return (
        <Field
          key={fieldDef.key}
          label={fieldDef.label}
          value={displayValue}
          locked={editing && !writable}
          // REG-812: click any field you may edit to start editing it.
          onActivate={!editing && writable && !lockHolder ? () => startEditing(fieldDef.key) : undefined}
          full={fieldDef.type === 'textarea'}
        />
      );
    }

    if (fieldDef.type === 'relationship') {
      const relationship = relationshipsByField[fieldDef.relationshipField || fieldDef.key];
      if (!relationship) {
        return <Field key={fieldDef.key} label={fieldDef.label} value={m[fieldDef.key]} />;
      }
      const relRecord = m[`${fieldDef.key}_rel`] as BaseRecord | null | undefined;
      return (
        <label key={fieldDef.key} className="sky-field">
          <div className="sky-field-label">{fieldDef.label}</div>
          <RecordLookupSelect
            name={fieldDef.key}
            relationship={relationship}
            record={relRecord && relRecord.id != null ? relRecord : null}
            schema="public"
            onChange={(record) => setField(fieldDef.key, record?.id ?? null)}
          />
        </label>
      );
    }

    if (fieldDef.type === 'picklist') {
      const options = picklistsByField[fieldDef.key]?.values || [];
      return (
        <label key={fieldDef.key} className="sky-field">
          <div className="sky-field-label">{fieldDef.label}</div>
          <select
            className="sky-field-input"
            autoFocus={focusKey === fieldDef.key}
            value={m[fieldDef.key] ?? ''}
            onChange={(e) => setField(fieldDef.key, e.target.value || null)}
          >
            <option value="">—</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>{option.label || option.value}</option>
            ))}
          </select>
        </label>
      );
    }

    if (fieldDef.type === 'textarea') {
      return (
        <label key={fieldDef.key} className="sky-field sky-field--full">
          <div className="sky-field-label">{fieldDef.label}</div>
          <textarea
            className="sky-field-input sky-field-input--textarea"
            autoFocus={focusKey === fieldDef.key}
            value={m[fieldDef.key] ?? ''}
            onChange={(e) => setField(fieldDef.key, e.target.value)}
          />
        </label>
      );
    }

    return (
      <label key={fieldDef.key} className="sky-field">
        <div className="sky-field-label">{fieldDef.label}</div>
        <input
          className="sky-field-input"
          type="text"
          autoFocus={focusKey === fieldDef.key}
          value={m[fieldDef.key] ?? ''}
          onChange={(e) => setField(fieldDef.key, e.target.value)}
        />
      </label>
    );
  };

  const editToolbar = editing ? (
    <div className="sky-edit-toolbar">
      <button className="sky-text-btn" disabled={saving} onClick={cancelEditing}>Cancel</button>
      <button className="sky-text-btn sky-text-btn--primary" disabled={saving} onClick={() => void handleSave()}>
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  ) : (
    <button
      className="sky-text-btn"
      title={lockHolder ? `${lockHolder.userName} is editing this merchant` : 'Edit these fields'}
      disabled={Boolean(lockHolder)}
      onClick={() => startEditing()}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
      Edit
    </button>
  );

  return (
    <>
      <SectionLabel right={editToolbar}>Basic Information</SectionLabel>
      {lockHolder && !editing && (
        <div className="sky-mp-empty">{lockHolder.userName} is editing this merchant.</div>
      )}
      <div className="sky-field-grid">
        {QUICK_FIELDS.map(renderField)}
        {renderField(NOTE_FIELD)}
      </div>

      <SectionLabel>Contact</SectionLabel>
      <div className="sky-field-grid">
        {CONTACT_FIELDS.map(renderField)}
      </div>

      <SectionLabel>Business Details</SectionLabel>
      <div className="sky-field-grid">
        {BUSINESS_FIELDS.map(renderField)}
        <Field label="SSN" value={m.ssn ? `•••-••-${String(m.ssn).slice(-4)}` : ''} locked />
      </div>

      <SectionLabel>Address</SectionLabel>
      <div className="sky-field-grid">
        {ADDRESS_FIELDS.map(renderField)}
        <Field label="Country" value={m.country} locked={editing} />
      </div>

      <SectionLabel>Banking</SectionLabel>
      <div className="sky-field-grid">
        <Field label="Bank Name" value={m.bank_name} locked />
        <Field label="Account Type" value={m.account_type_rel_name || m.account_type} locked />
        <Field label="Routing" value={m.routing_number ? `•••• ${String(m.routing_number).slice(-4)}` : ''} locked />
        <Field label="Account" value={m.account_number ? `•••• ${String(m.account_number).slice(-4)}` : ''} locked />
      </div>
    </>
  );
}

function Field({ label, value, locked, onActivate, full }: {
  label: string;
  value: any;
  locked?: boolean;
  onActivate?: () => void;
  full?: boolean;
}) {
  const display = value != null && value !== '' ? String(value) : '';
  const empty = !display;
  return (
    <div
      className={`sky-field${full ? ' sky-field--full' : ''}${onActivate ? ' sky-field--editable' : ''}`}
      onClick={onActivate}
      role={onActivate ? 'button' : undefined}
      tabIndex={onActivate ? 0 : undefined}
      title={onActivate ? `Edit ${label}` : undefined}
      onKeyDown={onActivate ? (event) => { if (event.key === 'Enter') onActivate(); } : undefined}
    >
      <div className="sky-field-label">{label}</div>
      <div className={`sky-field-value ${empty ? 'sky-field-value--empty' : ''} ${locked ? 'sky-field-value--locked' : ''}`}>
        {display || '—'}
      </div>
    </div>
  );
}

function formatMoney(v: any): string {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return '$' + n.toLocaleString('en-US');
}

export default React.memo(MerchantBasicInfoTab);
